import { Box, HStack, Heading, VStack, useToast } from '@chakra-ui/react';
import { InputFormControl } from '@jurnee/common/src/components/InputFormControl';
import { TextareaFormControl } from '@jurnee/common/src/components/TextareaFormControl';
import { PrimaryButton } from '@jurnee/common/src/components/primitives/buttons/PrimaryButton';
import { PartnerProviderInformationCreate } from '@jurnee/common/src/entities/PartnerProviderInformation';
import { Loader } from '@jurnee/common/src/components/Loader';
import { PlacePredictionJSON } from '@jurnee/common/src/entities/Place';
import { getErrorToast, getSuccessToast } from '@jurnee/common/src/utils/toasts';
import { AddressForm } from 'components/AddressForm';
import { GoogleMapsAccount } from 'components/GoogleMapsAccount';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store';
import { getPartnerSelector } from 'store/partner/partner.selectors';
import { createPartnerAddressThunk, createPartnerProviderInformationThunk, updatePartnerAddressThunk, updatePartnerThunk } from 'store/partner/partner.thunks';

export function Account() {
  const dispatch = useAppDispatch();
  const partner = useSelector(getPartnerSelector);
  const toast = useToast();
  const { t } = useTranslation(['common', 'toasts', 'account']);

  const [name, setName] = useState(partner?.name ?? '');
  const [description, setDescription] = useState(partner?.description ?? '');
  const [phoneNumber, setPhoneNumber] = useState(partner?.phoneNumber ?? '');
  const [email, setEmail] = useState(partner?.email ?? '');
  const [website, setWebsite] = useState(partner?.website ?? '');
  const [providerInformation, setProviderInformation] = useState<PartnerProviderInformationCreate | null>(null);

  const [address, setAddress] = useState({
    street: partner?.address?.street ?? null,
    city: partner?.address?.city ?? '',
    country: partner?.address?.country ?? '',
    countryCode: partner?.address?.countryCode ?? '',
    postalCode: partner?.address?.postalCode ?? null,
    state: partner?.address?.state ?? null
  });

  if (!partner) {
    return <Loader />;
  }

  const partnerId = partner.id;

  const existingProviderInformation = partner.partnersProvidersInformation.find(({ provider }) => {
    return provider === 'GOOGLE_MAPS';
  });

  function savePartner() {
    const data = { email, description, name, phoneNumber, website };

    return dispatch(updatePartnerThunk({
      id: partnerId,
      data
    }));
  }

  function saveAddress() {
    const { countryCode: _countryCode, ...data } = address;
    const addressData = {
      ...data,
      country: address.country,
      city: address.city
    };

    const partnerAddress = partner?.address;

    if (partnerAddress) {
      return dispatch(updatePartnerAddressThunk({
        id: partnerId,
        addressId: partnerAddress.id,
        data: addressData
      }));
    } else {
      return dispatch(createPartnerAddressThunk({
        id: partnerId,
        data: addressData
      }));
    }
  }

  async function onSave() {
    try {
      if (existingProviderInformation === undefined && providerInformation) {
        await dispatch(createPartnerProviderInformationThunk({ id: partnerId, data: providerInformation })).unwrap();
      }

      await Promise.all([
        savePartner().unwrap(),
        saveAddress().unwrap()
      ]);

      toast(getSuccessToast(t('toasts:accountUpdate.success')));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      toast(getErrorToast(t('toasts:accountUpdate.error'), message));
    }
  }

  function onPlaceChange({ place }: PlacePredictionJSON) {
    if (!place) {
      return;
    }

    setName(place.name);

    if (place.address) {
      setAddress({
        street: place.address.street ?? null,
        city: place.address.city ?? '',
        country: place.address.country ?? '',
        countryCode: place.address.countryCode ?? '',
        postalCode: place.address.postalCode ?? null,
        state: null
      });
    }

    setPhoneNumber(place.phoneNumber ? place.phoneNumber.replace(/-/g, ' ') : '');
    setWebsite(place.websiteUrl ?? '');

    if (place.rating && place.providerUrl) {
      setProviderInformation({
        partnerId,
        provider: 'GOOGLE_MAPS',
        providerId: place.id,
        ratingAverage: place.rating.average ?? 0,
        ratingCount: place.rating.count ?? 0,
        url: place.providerUrl
      });
    }
  }

  return (
    <VStack w="100%" h="100%" spacing={0}>
      <Box w="100%" p={8} bg="white" borderBottom="1px solid" borderColor="gray.200">
        <Heading lineHeight="32px">{t('account:heading')}</Heading>
      </Box>

      <HStack w="100%" maxW="1184px" p={8} h="100%" alignSelf="center" spacing={8} justifyContent="center">
        <VStack w="50%" spacing={5} h="100%">
          <InputFormControl name="name" value={name} onChange={setName} isRequired/>

          <AddressForm address={address} onChange={setAddress} />

          <HStack w="100%" spacing={5}>
            <InputFormControl name="phoneNumber" value={phoneNumber} onChange={setPhoneNumber} isRequired />
            <InputFormControl name="email" type="email" value={email} onChange={setEmail} isRequired />
          </HStack>
          <InputFormControl name="website" type="url" value={website} onChange={setWebsite} />
        </VStack>

        <VStack w="50%" spacing={8} h="100%" pt={6}>
          <GoogleMapsAccount providerInformation={existingProviderInformation} onChange={onPlaceChange}/>
          <TextareaFormControl name="description" h={246} value={description} onChange={setDescription} isRequired/>
        </VStack>
      </HStack>

      <HStack w="100%" px={8} py={4} bg="white" borderTop="1px solid" borderColor="gray.200" position="sticky" bottom={0} justifyContent="flex-end">
        <PrimaryButton colorScheme="teal" size="sm" onClick={onSave}>{t('common:buttons.save')}</PrimaryButton>
      </HStack>
    </VStack>
  );
}